import {useCallback, useContext} from 'react';

import {ValidationContext} from 'src/contexts/validation-context';
import {useCommonDataHook} from 'src/hooks/use-common-data-hook';
import {getPreset, updatePreset} from 'src/openapi/admin/apis/EntitlementsPresetApi';
import {useAppDispatch} from 'src/store/hooks';

import {
    PRESET_NAME_CONTROL,
    PRESET_NAME_REQUIRED_ERROR,
    PRESET_NAME_REQUIRED_MESSAGE,
} from './constants';

type Props = {
    presetId: string,
};

export const useEditPreset = ({presetId}: Props) => {
    const apiDispatch = useAppDispatch();
    const {addError, removeError} = useContext(ValidationContext);

    const getDispatchAction = useCallback(async () => {
        const response = await apiDispatch(getPreset({presetId}));
        if (response.payload.success) {
            const payload: {success: true, error: false, data: {name: string, description?: string}} = {
                success: true,
                error: false,
                data: {
                    name: response.payload.data.name,
                    description: response.payload.data.description,
                },
            };

            return Promise.resolve({type: '', payload});
        } else {
            return Promise.resolve({type: '', payload: response.payload});
        }
    }, [apiDispatch, presetId]);

    const saveDispatchAction = useCallback((
        updatePresetRequest: {name: string, description?: string},
        signal: AbortSignal,
        reason?: string,
    ) => apiDispatch(
        updatePreset({updatePresetRequest: {...updatePresetRequest, reason: reason || ''}, presetId}, {signal}),
    ), [presetId, apiDispatch]);

    const editValidation = useCallback((data: {name: string, description?: string}) => {
        if (data.name) {
            removeError(PRESET_NAME_CONTROL);
        }
    }, [removeError]);

    const saveValidation = useCallback((data: {name: string, description?: string}) => {
        let valid = true;

        if (!data.name) {
            addError({
                controlId: PRESET_NAME_CONTROL,
                code: PRESET_NAME_REQUIRED_ERROR,
                message: PRESET_NAME_REQUIRED_MESSAGE,
            });
            valid = false;
        }

        return valid;
    }, [addError]);

    return useCommonDataHook({
        initValue: {
            name: '',
        },
        getDispatchAction,
        saveDispatchAction,
        editValidation,
        saveValidation,
    });
};
